import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  organizationApi,
  type OrganizationMembersResponse,
  type PendingInvitationItem,
} from '@/manage/api/organization'
import { useUserStore } from '@/manage/stores/user'

export const useOrganizationStore = defineStore('organization', () => {
  const userStore = useUserStore()

  const currentOrg = ref<Record<string, any> | null>(null)
  const pendingInvitations = ref<PendingInvitationItem[]>([])
  const membersData = ref<OrganizationMembersResponse | null>(null)
  const isLoading = ref(false)
  const isActionLoading = ref(false)

  const isEnterpriseMode = computed(() => {
    return Boolean(userStore.asset?.is_enterprise_mode || currentOrg.value?.has_organization)
  })

  const isEnterpriseAdmin = computed(() => {
    return currentOrg.value?.member?.role === 'admin' || userStore.asset?.organization?.role === 'admin'
  })

  const enterpriseSummary = computed(() => membersData.value?.summary || null)
  const membersList = computed(() => membersData.value?.members || [])

  async function fetchCurrentOrg() {
    try {
      const data = await organizationApi.getCurrentOrganization()
      currentOrg.value = data
      return data
    } catch {
      currentOrg.value = null
      return null
    }
  }

  async function fetchPendingInvitations() {
    try {
      const list = await organizationApi.getPendingInvitations()
      pendingInvitations.value = list
      return list
    } catch {
      pendingInvitations.value = []
      return []
    }
  }

  async function fetchMembers() {
    isLoading.value = true
    try {
      const res = await organizationApi.getOrganizationMembers()
      membersData.value = res
      return res
    } finally {
      isLoading.value = false
    }
  }

  async function inviteMember(email: string) {
    isActionLoading.value = true
    try {
      const res = await organizationApi.inviteMember(email)
      await fetchMembers()
      return res
    } finally {
      isActionLoading.value = false
    }
  }

  async function cancelInvite(memberId: string) {
    isActionLoading.value = true
    try {
      const res = await organizationApi.cancelInvitation(memberId)
      await fetchMembers()
      return res
    } finally {
      isActionLoading.value = false
    }
  }

  async function adjustQuota(
    memberId: string,
    pointsType: 'ai_match' | 'contact',
    targetAmount: number,
  ) {
    isActionLoading.value = true
    try {
      const res = await organizationApi.adjustMemberQuota(memberId, {
        points_type: pointsType,
        target_amount: targetAmount,
      })
      await fetchMembers()
      return res
    } finally {
      isActionLoading.value = false
    }
  }

  async function removeMember(memberId: string) {
    isActionLoading.value = true
    try {
      const res = await organizationApi.removeMember(memberId)
      await fetchMembers()
      return res
    } finally {
      isActionLoading.value = false
    }
  }

  async function respondInvitation(memberId: string, accept: boolean) {
    isActionLoading.value = true
    try {
      const res = await organizationApi.respondToInvitation(memberId, accept)
      await fetchPendingInvitations()
      await userStore.fetchAsset(true)
      await fetchCurrentOrg()
      return res
    } finally {
      isActionLoading.value = false
    }
  }

  async function leaveOrg() {
    isActionLoading.value = true
    try {
      const res = await organizationApi.leaveOrganization()
      await userStore.fetchAsset(true)
      await fetchCurrentOrg()
      return res
    } finally {
      isActionLoading.value = false
    }
  }

  return {
    currentOrg,
    pendingInvitations,
    membersData,
    isLoading,
    isActionLoading,
    isEnterpriseMode,
    isEnterpriseAdmin,
    enterpriseSummary,
    membersList,
    fetchCurrentOrg,
    fetchPendingInvitations,
    fetchMembers,
    inviteMember,
    cancelInvite,
    adjustQuota,
    removeMember,
    respondInvitation,
    leaveOrg,
  }
})
